import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, Gift, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminGetUserPremiumCourtesy,
  adminGrantUserPremiumCourtesy,
  adminRevokeUserPremiumCourtesy,
} from '@/lib/api';

const DURATION_OPTIONS = [
  { value: '7', label: '7 días' },
  { value: '15', label: '15 días' },
  { value: '30', label: '1 mes' },
  { value: '90', label: '3 meses' },
  { value: '180', label: '6 meses' },
  { value: 'custom', label: 'Fecha personalizada' },
];

function toLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (_) {
    return '';
  }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
}

export function AdminUserPremiumCard({ userId, userActive }) {
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [state, setState] = useState({ active: null, last: null });
  const [durationPreset, setDurationPreset] = useState('30');
  const [customEndsAt, setCustomEndsAt] = useState('');
  const [reason, setReason] = useState('');

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await adminGetUserPremiumCourtesy(userId);
      setState({ active: data?.active || null, last: data?.last || null });
    } catch (e) {
      toast.error(e?.message || 'No se pudo cargar el estado de cortesía.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleGrant = async () => {
    if (!userId) return;
    if (!userActive) {
      toast.error('El usuario debe estar activo para conceder Premium.');
      return;
    }
    const payload = {};
    if (durationPreset === 'custom') {
      if (!customEndsAt) { toast.error('Indica fecha de caducidad.'); return; }
      try {
        payload.endsAt = new Date(customEndsAt).toISOString();
      } catch (_) { toast.error('Fecha no válida.'); return; }
    } else {
      payload.days = parseInt(durationPreset, 10);
    }
    if (reason.trim()) payload.reason = reason.trim();
    setWorking(true);
    try {
      await adminGrantUserPremiumCourtesy(userId, payload);
      toast.success('Premium de cortesía concedido.');
      setReason('');
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'No se pudo conceder la cortesía.');
    } finally {
      setWorking(false);
    }
  };

  const handleRevoke = async () => {
    if (!state.active) return;
    if (!window.confirm('¿Revocar el Premium de cortesía? El usuario perderá los beneficios no comerciales.')) return;
    setWorking(true);
    try {
      await adminRevokeUserPremiumCourtesy(userId);
      toast.success('Cortesía revocada.');
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'No se pudo revocar.');
    } finally {
      setWorking(false);
    }
  };

  const active = state.active;
  const last = state.last;

  // Fecha efectiva de caducidad según la selección actual (preview antes
  // de confirmar). No se persiste.
  const previewEndsAt = (() => {
    try {
      if (durationPreset === 'custom') {
        return customEndsAt ? new Date(customEndsAt) : null;
      }
      const days = parseInt(durationPreset, 10);
      if (!Number.isFinite(days) || days <= 0) return null;
      const d = new Date();
      d.setDate(d.getDate() + days);
      return d;
    } catch (_) { return null; }
  })();
  const previewExceedsLimit = (() => {
    if (!previewEndsAt) return false;
    const maxMs = Date.now() + 365 * 24 * 60 * 60 * 1000;
    return previewEndsAt.getTime() > maxMs;
  })();

  return (
    <Card className="bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)]" data-testid="admin-user-premium-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold flex items-center gap-1.5">
            <Gift className="h-3.5 w-3.5 text-[var(--ns-gold-700)]" /> Premium de cortesía
          </p>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-[var(--ns-text-muted)]" />}
        </div>

        {active ? (
          <div className="mb-4 p-3 rounded-xl border border-emerald-200 bg-emerald-50">
            <div className="flex items-center justify-between">
              <Badge className="bg-emerald-600 text-white">Activa</Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRevoke}
                disabled={working}
                className="h-8 text-red-600 hover:bg-red-50"
                data-testid="admin-user-premium-revoke"
              >
                {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                <span className="ml-1">Revocar</span>
              </Button>
            </div>
            <div className="mt-2 text-sm space-y-0.5">
              <div><span className="text-[var(--ns-text-muted)]">Inicio:</span> {formatDateTime(active.starts_at)}</div>
              <div><span className="text-[var(--ns-text-muted)]">Caducidad:</span> <strong>{formatDateTime(active.ends_at)}</strong></div>
              {active.notes && (
                <div className="text-xs text-[var(--ns-text-muted)] italic mt-1 break-all">{active.notes}</div>
              )}
            </div>
            <div className="mt-2 text-[11px] text-[var(--ns-text-muted)]">
              No es una suscripción comercial: sin cobros, sin renovación automática.
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 rounded-xl border border-dashed border-[var(--ns-border)] bg-[var(--ns-cream)]/30">
            <div className="text-sm text-[var(--ns-text-muted)]">
              El usuario no tiene Premium de cortesía activo.
              {last && last.status === 'revoked' && (
                <div className="mt-1 text-xs">Última revocada el {formatDateTime(last.updated_at)}.</div>
              )}
              {last && last.status === 'active' && last.ends_at && new Date(last.ends_at) <= new Date() && (
                <div className="mt-1 text-xs">Última caducó el {formatDateTime(last.ends_at)}.</div>
              )}
            </div>
          </div>
        )}

        {!active && (
          <div className="space-y-3" data-testid="admin-user-premium-grant-form">
            <div>
              <Label className="text-xs text-[var(--ns-text-muted)]">Duración</Label>
              <Select value={durationPreset} onValueChange={setDurationPreset}>
                <SelectTrigger className="h-10 bg-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {durationPreset === 'custom' && (
              <div>
                <Label className="text-xs text-[var(--ns-text-muted)]">Caduca el (máx. 365 días)</Label>
                <Input
                  type="datetime-local"
                  value={customEndsAt}
                  max={(() => {
                    const d = new Date();
                    d.setDate(d.getDate() + 365);
                    const pad = (n) => String(n).padStart(2, '0');
                    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                  })()}
                  min={(() => {
                    const d = new Date();
                    const pad = (n) => String(n).padStart(2, '0');
                    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                  })()}
                  onChange={(e) => setCustomEndsAt(e.target.value)}
                  className="mt-1"
                  data-testid="admin-user-premium-custom-ends-at"
                />
              </div>
            )}
            <div>
              <Label className="text-xs text-[var(--ns-text-muted)]">Motivo (opcional)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="p.ej. soporte comercial"
                maxLength={500}
                className="mt-1"
                data-testid="admin-user-premium-reason"
              />
            </div>
            <Button
              onClick={handleGrant}
              disabled={working || !userActive || previewExceedsLimit || (durationPreset === 'custom' && !customEndsAt)}
              className="w-full h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
              data-testid="admin-user-premium-grant-btn"
            >
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Conceder Premium de cortesía'}
            </Button>
            {previewEndsAt && (
              <div className="text-xs text-[var(--ns-text-muted)] text-center" data-testid="admin-user-premium-preview">
                Caducará el <strong className={previewExceedsLimit ? 'text-red-600' : 'text-[var(--ns-text)]'}>
                  {formatDateTime(previewEndsAt.toISOString())}
                </strong>
                {previewExceedsLimit && <span className="block text-red-600 mt-0.5">Supera el máximo de 365 días.</span>}
              </div>
            )}
            {!userActive && (
              <div className="text-xs text-amber-700">
                El usuario debe estar activo para recibir la cortesía.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
